/**
 * Build the Texas Momentum newsletter HTML to match design:
 * #fff8ed background, red header (TEXAS MOMENTUM / STARTUPS), black subtitle bar,
 * timeline with large red circles (logo inside), small hollow red circles, vertical line,
 * light gray cards, black rectangular LEARN MORE. Sent via EmailJS.
 */

const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://txmomentum.org';
const LINKEDIN_URL = 'https://www.linkedin.com/company/txmomentum';
const RED = '#f63113';
const BG_BEIGE = '#fff8ed';

// Same border width for red line, logo circle, and mini circle (visual consistency).
const RED_BORDER_WIDTH_PX = 3;

// Per-post excerpt length in newsletter (enough for a hook, then "Learn more" for full post).
// ~350 chars ≈ 2–3 sentences; aligns with common teaser lengths (300–500) and digest best practices.
const NEWSLETTER_POST_CHAR_LIMIT = 500;

// White LinkedIn icon as data URL (email-safe; many clients strip or misrender SVG fill)
const LINKEDIN_ICON_DATA = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20"><path fill="#ffffff" d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>');

function escapeHtml(text) {
  if (!text) return '';
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Large hollow red circle (outline only) with logo inside; border width matches line and mini circle. */
function buildLogoCircleHtml(item) {
  const LOGO_OUTER = 80;
  const LOGO_INNER = 66;
  const logoInner = item.logoDataUrl
    ? `<img src="${item.logoDataUrl}" alt="${escapeHtml(item.startupName)}" width="${LOGO_INNER}" height="${LOGO_INNER}" style="width:${LOGO_INNER}px;height:${LOGO_INNER}px;border-radius:50%;object-fit:contain;display:block;background:${BG_BEIGE};">`
    : `<div style="background:#eee;width:${LOGO_INNER}px;height:${LOGO_INNER}px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:#4A4A4A;font-size:11px;">Logo</div>`;
  return `<div style="width:${LOGO_OUTER}px;height:${LOGO_OUTER}px;border-radius:50%;border:${RED_BORDER_WIDTH_PX}px solid ${RED};background:${BG_BEIGE};display:inline-flex;align-items:center;justify-content:center;box-sizing:border-box;"><div style="width:${LOGO_INNER}px;height:${LOGO_INNER}px;overflow:hidden;border-radius:50%;background:${BG_BEIGE};">${logoInner}</div></div>`;
}

/**
 * Build one card's HTML: light gray block, title/tagline, post text, LEARN MORE (black, rectangular, caps).
 */
function buildCardHtml(item) {
  const name = escapeHtml(item.startupName);
  const tagline = escapeHtml(item.founderNames || '');
  let paragraphs = [];
  if (item.latestPost && item.latestPost.content) {
    paragraphs = item.latestPost.content
      .trim()
      .split(/\n+/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (paragraphs.length === 0 && item.latestPost && item.latestPost.title) {
    paragraphs = [item.latestPost.title];
  }
  if (paragraphs.length === 0) {
    paragraphs = ['More updates coming soon!'];
  }
  let teaserText = paragraphs.join(' ').trim();
  if (teaserText.length > NEWSLETTER_POST_CHAR_LIMIT) {
    teaserText = teaserText.slice(0, NEWSLETTER_POST_CHAR_LIMIT);
    const lastSpace = teaserText.lastIndexOf(' ');
    if (lastSpace > NEWSLETTER_POST_CHAR_LIMIT * 0.6) teaserText = teaserText.slice(0, lastSpace);
    teaserText += '…';
  }
  const postHtml = `<p style="margin:0 0 8px;padding:0;font-size:14px;color:#141414;line-height:1.5;">${escapeHtml(teaserText)}</p>`;
  const blogUrl = `${SITE_URL}/blog/${encodeURIComponent(item.startupName)}`;

  return `
    <div style="margin:0;overflow:visible;">
      <div style="background:#E8EDED;border-radius:12px;padding:16px 14px;margin:0;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="border-collapse:collapse;">
          <tr>
            <td style="vertical-align:top;padding:0;">
              <h2 style="font-size:18px;font-weight:700;color:#141414;margin:0 0 4px;padding:0;">${name}</h2>
              <p style="font-size:13px;color:#4A4A4A;margin:0 0 8px;padding:0;">${tagline}</p>
            </td>
          </tr>
          <tr>
            <td style="vertical-align:top;padding:0;padding-top:4px;">${postHtml}</td>
          </tr>
        </table>
      </div>
      <div style="text-align:right;margin-top:-15px;padding-right:14px;position:relative;z-index:1;">
        <a href="${escapeHtml(blogUrl)}" style="display:inline-block;background:#141414;color:#fff !important;padding:8px 16px;border-radius:6px;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:0.05em;text-decoration:none;">LEARN MORE</a>
      </div>
    </div>`;
}

/**
 * One row: timeline (mini circle + logo circle) + card. Red line is one continuous bar via cards-wrap border-left.
 */
function buildCardRow(item, isFirst, isLast) {
  const cardInner = buildCardHtml(item);
  const rowPadding = isLast ? '0' : '24px';
  const LINE_PAD = 10;
  const MINI_SIZE = 10;
  const lineCenterPx = RED_BORDER_WIDTH_PX / 2;
  const miniOffset = 11 - lineCenterPx;
  const smallHollow = `<div style="width:${MINI_SIZE}px;height:${MINI_SIZE}px;border-radius:50%;border:${RED_BORDER_WIDTH_PX}px solid ${RED};background:${BG_BEIGE};margin-left:-${miniOffset}px;display:inline-block;vertical-align:middle;"></div>`;
  const logoCircle = buildLogoCircleHtml(item);

  const nodeContent = `<td style="vertical-align:middle;width:10px;padding:0;text-align:center;line-height:0;">${smallHollow}</td><td style="vertical-align:middle;padding:0 0 0 ${LINE_PAD}px;text-align:left;">${logoCircle}</td>`;
  const timelineCell = `<td class="timeline-cell" style="vertical-align:middle;padding-bottom:${rowPadding};padding-right:6px;padding-left:0;"><table cellpadding="0" cellspacing="0" border="0" width="100%" style="vertical-align:middle;"><tr style="vertical-align:middle;">${nodeContent}</tr></table></td>`;
  const cardCell = `<td class="card-cell" style="vertical-align:top;padding-bottom:${rowPadding};width:100%;">${cardInner}</td>`;

  return `<tr style="vertical-align:middle;">${timelineCell}${cardCell}</tr>`;
}

const NEWSLETTER_HTML_TEMPLATE = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Texas Momentum Startups</title>
  <style>
    body { margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background: #fff8ed; }
    .wrapper { max-width: 600px; margin: 0 auto; background: #fff8ed; }
    .header { background: #f63113; color: #fff; padding: 28px 24px 36px; text-align: center; }
    .header h1 { margin: 0; font-size: 28px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.02em; color: #fff; }
    .header .header-startups { display: block; margin-top: 4px; font-size: 36px; font-weight: 800; color: #141414; text-transform: uppercase; }
    .header-sub { background: #141414; color: #fff; padding: 12px 24px; text-align: center; max-width: 75%; margin: -20px auto 0; position: relative; z-index: 1; box-sizing: border-box; }
    .header-sub p { margin: 0; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; color: #fff; }
    .content { padding: 24px 24px 32px; background: #fff8ed; }
    .cards-wrap { border-left: 3px solid #f63113; padding-left: 0; }
    .cards-table { border-collapse: collapse; width: 100%; }
    .timeline-cell { width: 72px; }
    .footer { background: #f63113; color: #fff; padding: 20px 24px; font-size: 13px; width: 100%; box-sizing: border-box; }
    .footer-tbl { width: 100%; border: 0; }
    .footer-td { text-align: center; vertical-align: middle; color: #ffffff; }
    .footer-logo { width: 28px; height: 28px; display: inline-block; vertical-align: middle; }
    .footer-linkedin { color: #ffffff !important; text-decoration: none; }
  </style>
</head>
<body style="background:#fff8ed;">
  <div class="wrapper" style="background:#fff8ed;">
    <div class="header" style="background:#f63113;color:#fff;padding:28px 24px 36px;text-align:center;">
      <h1 style="margin:0;font-size:28px;font-weight:700;text-transform:uppercase;letter-spacing:0.02em;color:#fff;">TEXAS MOMENTUM</h1>
      <p class="header-startups" style="margin:4px 0 0;font-size:36px;font-weight:800;color:#141414;text-transform:uppercase;">STARTUPS</p>
    </div>
    <div class="header-sub" style="background:#141414;color:#fff;padding:12px 24px;text-align:center;max-width:75%;margin:-20px auto 0;position:relative;z-index:1;box-sizing:border-box;">
      <p style="margin:0;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.08em;color:#fff;">A CURATED DIGEST OF BUILDERS FROM OUR COMMUNITY</p>
    </div>
    <div class="content" style="padding:24px 24px 32px;background:#fff8ed;">
      <div class="cards-wrap" style="border-left:3px solid #f63113;padding-left:0;">
        <table class="cards-table" cellpadding="0" cellspacing="0" border="0" width="100%">
          {{STARTUP_CARDS}}
        </table>
      </div>
    </div>
    <div class="footer" style="background:#f63113;color:#fff;padding:20px 24px;font-size:13px;">
      <table class="footer-tbl" cellpadding="0" cellspacing="0" border="0" align="center">
        <tr>
          <td class="footer-td" align="center" style="color:#ffffff;">
            <a href="${SITE_URL}" style="color:#ffffff;text-decoration:none;" class="footer-logo" aria-label="Texas Momentum"><img src="${SITE_URL}/favicon.png" alt="Texas Momentum" width="28" height="28" style="display:inline-block;width:28px;height:28px;vertical-align:middle;"></a>
            <span style="margin:0 10px;color:#ffffff;">Built by Texas Momentum · Austin, TX</span>
            <a href="${LINKEDIN_URL}" target="_blank" rel="noopener noreferrer" class="footer-linkedin" style="color:#ffffff !important;text-decoration:none;" aria-label="LinkedIn"><img src="${LINKEDIN_ICON_DATA}" alt="LinkedIn" width="20" height="20" style="display:inline-block;width:20px;height:20px;vertical-align:middle;"></a>
          </td>
        </tr>
      </table>
    </div>
  </div>
</body>
</html>`;

/**
 * Build full newsletter HTML from getNewsletterContent() items.
 * @param {Array<{ startupName: string, founderNames: string, latestPost?: { title: string, content: string } }>} items
 * @returns {string} Full HTML document
 */
export function buildNewsletterHtml(items) {
  const list = items || [];
  const cardsHtml = list.length > 0
    ? list.map((item, i) => buildCardRow(item, i === 0, i === list.length - 1)).join('')
    : '<tr><td colspan="2" style="padding:20px;color:#4A4A4A;font-size:15px;">No cohort updates this time. Check back soon!</td></tr>';
  return NEWSLETTER_HTML_TEMPLATE.replace('{{STARTUP_CARDS}}', cardsHtml);
}

/**
 * Send the newsletter via EmailJS.
 * Requires REACT_APP_EMAILJS_SERVICE_ID, REACT_APP_EMAILJS_TEMPLATE_ID, REACT_APP_EMAILJS_PUBLIC_KEY in env.
 * The EmailJS template should have: To = {{to_email}}, Subject = {{subject}}, Body = {{{html_content}}} (triple braces for raw HTML).
 *
 * @param {{ subject: string, htmlBody: string, items: Array }} newsletterContent - from getNewsletterContent()
 * @param {string} toEmail - recipient
 * @returns {Promise<void>}
 */
export async function sendNewsletterWithEmailJS(newsletterContent, toEmail) {
  const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
  const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
  const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

  if (!serviceId || !templateId || !publicKey) {
    throw new Error(
      'EmailJS not configured. Add REACT_APP_EMAILJS_SERVICE_ID, REACT_APP_EMAILJS_TEMPLATE_ID, and REACT_APP_EMAILJS_PUBLIC_KEY to .env.local'
    );
  }

  const htmlContent = buildNewsletterHtml(newsletterContent.items);

  const { default: emailjs } = await import('@emailjs/browser');

  await emailjs.send(serviceId, templateId, {
    to_email: toEmail,
    subject: newsletterContent.subject,
    html_content: htmlContent,
  }, publicKey);
}
